(() => {
  'use strict';

  const GLOBAL_KEY = 'RFWorldCardsAutoplay';

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].initAll();
    return;
  }

  const SECTION_SELECTOR = '[data-rf-world-cards]';
  const TRACK_SELECTOR = '[data-rf-world-cards-track]';
  const CARD_SELECTOR = '[data-rf-world-card]';
  const controllers = new WeakMap();

  function parseBoolean(value) {
    return value === 'true';
  }

  function createController(section) {
    const track = section.querySelector(TRACK_SELECTOR);
    const cards = Array.from(section.querySelectorAll(CARD_SELECTOR));
    const autoRotate = parseBoolean(section.dataset.rfAutoRotate);
    const seconds = Number.parseInt(section.dataset.rfAutoRotateSeconds, 10);
    const intervalMs = Number.isFinite(seconds) ? Math.max(2, seconds) * 1000 : 5000;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const hasIntersectionObserver = 'IntersectionObserver' in window;

    if (!track || cards.length < 2 || !autoRotate) {
      return { destroy() {} };
    }

    let timerId = 0;
    let isVisible = !hasIntersectionObserver;
    let isHovering = false;
    let hasFocus = false;
    let isUserPaused = false;
    let isEditorPaused = false;
    let isPageScrolling = false;
    let pageScrollTimer = 0;
    const listeners = [];

    function listen(target, eventName, handler, options) {
      target.addEventListener(eventName, handler, options);
      listeners.push(() => target.removeEventListener(eventName, handler, options));
    }

    function shouldRun() {
      return (
        !document.hidden &&
        isVisible &&
        !isHovering &&
        !hasFocus &&
        !isUserPaused &&
        !isEditorPaused &&
        !isPageScrolling &&
        !reduceMotion.matches
      );
    }

    function stop() {
      if (timerId) {
        window.clearTimeout(timerId);
        timerId = 0;
      }
    }

    function schedule() {
      stop();
      if (!shouldRun()) return;
      timerId = window.setTimeout(advance, intervalMs);
    }

    function getCurrentIndex() {
      const trackCenter = track.scrollLeft + track.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const distance = Math.abs(trackCenter - cardCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    function scrollCardIntoView(card) {
      const left = card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;
      const maxLeft = Math.max(0, track.scrollWidth - track.clientWidth);

      track.scrollTo({
        left: Math.min(Math.max(0, left), maxLeft),
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    }

    function advance() {
      timerId = 0;
      if (!shouldRun()) return;

      const nextIndex = (getCurrentIndex() + 1) % cards.length;
      scrollCardIntoView(cards[nextIndex]);
      schedule();
    }

    function pauseAfterUserInteraction() {
      isUserPaused = true;
      stop();
    }

    const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (supportsHover) {
      listen(section, 'mouseenter', () => {
        isHovering = true;
        stop();
      });

      listen(section, 'mouseleave', () => {
        isHovering = false;
        schedule();
      });
    }

    listen(section, 'focusin', () => {
      hasFocus = true;
      stop();
    });

    listen(section, 'focusout', (event) => {
      if (!section.contains(event.relatedTarget)) {
        hasFocus = false;
        schedule();
      }
    });

    listen(track, 'pointerdown', pauseAfterUserInteraction, { passive: true });
    listen(track, 'touchstart', pauseAfterUserInteraction, { passive: true });
    listen(track, 'wheel', pauseAfterUserInteraction, { passive: true });
    listen(track, 'keydown', (event) => {
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', ' '].includes(event.key)) {
        pauseAfterUserInteraction();
      }
    });

    // Do not animate the horizontal story rail while the shopper is actively
    // scrolling the page vertically. This avoids competing motion on mobile.
    listen(window, 'scroll', () => {
      if (!isVisible) return;
      isPageScrolling = true;
      stop();
      if (pageScrollTimer) window.clearTimeout(pageScrollTimer);
      pageScrollTimer = window.setTimeout(() => {
        pageScrollTimer = 0;
        isPageScrolling = false;
        schedule();
      }, 450);
    }, { passive: true });

    listen(document, 'visibilitychange', schedule);

    const onMotionChange = () => schedule();
    if (typeof reduceMotion.addEventListener === 'function') {
      reduceMotion.addEventListener('change', onMotionChange);
      listeners.push(() => reduceMotion.removeEventListener('change', onMotionChange));
    } else if (typeof reduceMotion.addListener === 'function') {
      reduceMotion.addListener(onMotionChange);
      listeners.push(() => reduceMotion.removeListener(onMotionChange));
    }

    let observer;
    if (hasIntersectionObserver) {
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          isVisible = Boolean(entry && entry.isIntersecting);
          schedule();
        },
        { threshold: 0.15 }
      );
      observer.observe(section);
    }

    function handleBlockSelect(event) {
      if (!section.contains(event.target)) return;

      const selectedCard = event.target.closest(CARD_SELECTOR);
      isEditorPaused = true;
      stop();

      if (selectedCard) {
        scrollCardIntoView(selectedCard);
      }
    }

    function handleBlockDeselect(event) {
      if (!section.contains(event.target)) return;
      isEditorPaused = false;
      schedule();
    }

    if (window.Shopify && window.Shopify.designMode) {
      listen(document, 'shopify:block:select', handleBlockSelect);
      listen(document, 'shopify:block:deselect', handleBlockDeselect);
    }

    schedule();

    return {
      destroy() {
        stop();
        if (pageScrollTimer) window.clearTimeout(pageScrollTimer);
        listeners.forEach((remove) => remove());
        if (observer) observer.disconnect();
      }
    };
  }

  function init(section) {
    if (!section || controllers.has(section)) return;
    controllers.set(section, createController(section));
  }

  function destroy(section) {
    const controller = controllers.get(section);
    if (!controller) return;
    controller.destroy();
    controllers.delete(section);
  }

  function initAll(root = document) {
    if (root.matches && root.matches(SECTION_SELECTOR)) {
      init(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(SECTION_SELECTOR).forEach(init);
    }
  }

  window[GLOBAL_KEY] = { initAll };

  initAll();

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches(SECTION_SELECTOR)
      ? event.target
      : event.target.querySelector(SECTION_SELECTOR);

    destroy(section);
  });
})();
